/**
 * Ticker-synced, pausable timer system (verbatim port of debugbase DelayHelper).
 *
 * NOT a setTimeout wrapper: timers only advance when tick() runs, which the app
 * kernel calls once per animation frame (see App._onTick). That means
 * timers freeze with the app (pause()/appIsPaused) and stay in step with the
 * animation clock — replacing these with native timers desynchronizes
 * choreography that assumes tick-time, so don't.
 *
 * API quirk preserved from the original: pause(delay) *schedules* the pause
 * `delay` ms out (default 1000) rather than pausing immediately; unpause()
 * cancels a scheduled pause and resumes.
 */
interface Timer {
	type: 'TIMEOUT';
	closure: (() => void) | null;
	triggerAt: number;
	count: number;
	id: number | string;
	group: string | null;
}

export default {
	timers: [] as Timer[],
	lastTime: Date.now(),
	id: 1,
	idCount: 1,
	paused: false,
	pauseInterval: null as ReturnType<typeof setTimeout> | null,

	setTimeout(closure: () => void, triggerAt: number, id: number | string | null = null, group: string | null = null): number | string {
		const useId = id || this.idCount;
		this.timers.push({
			type: 'TIMEOUT',
			closure: closure,
			triggerAt: triggerAt,
			count: 0,
			id: useId,
			group: group
		});

		this.idCount++;

		return useId;
	},

	clearTimeout(id: number | string | null): void {
		if (!id) {
			return;
		}

		let i = 0;
		while (i < this.timers.length) {
			if (this.timers[i].id == id) {
				this.timers[i].closure = null;
				this.timers.splice(i, 1);
				break;
			}
			i++;
		}
	},

	clearGroup(group: string): void {
		let i = 0;
		while (i < this.timers.length) {
			if (this.timers[i].group == group) {
				this.timers[i].closure = null;
				this.timers.splice(i, 1);
				continue;
			}
			i++;
		}
	},

	pause(delay: number = 1000): void {
		if (!delay) this.paused = true;

		clearTimeout(this.pauseInterval);

		this.pauseInterval = setTimeout(() => {
			this.paused = true;
			this.pauseInterval = null;
		}, delay);
	},

	unpause(): void {
		clearTimeout(this.pauseInterval);
		this.paused = false;
	},

	tick(): void {
		if (this.paused) {
			this.lastTime = Date.now();
			return;
		}

		let i = 0;

		const time = Date.now();
		const delta = time - this.lastTime;

		while (i < this.timers.length) {
			this.timers[i].count += delta;

			if (this.timers[i].count >= this.timers[i].triggerAt) {
				this.timers[i].closure();

				if (this.timers[i]) {
					this.timers[i].closure = null;
					this.timers.splice(i, 1);
				}
			} else {
				i++;
			}
		}

		this.lastTime = time;
	}
};
